import { ReactNode, useEffect, useState } from 'react';
import { Badge, Box, Collapse, Flex, Spacer } from '@chakra-ui/react';

import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';

const SquircleNumber = ({
  active,
  children,
}: {
  children: ReactNode;
  active: boolean;
}) => (
  <Box
    alignItems="center"
    borderColor="grey.300"
    borderRadius="radius-10"
    borderWidth="1px"
    boxSize="space-40"
    display="flex"
    fontSize="font-18"
    fontWeight="weight-500"
    justifyContent="center"
    lineHeight="none"
    {...(active
      ? {
          backgroundColor: 'grey.800',
          color: 'white',
        }
      : {
          backgroundColor: 'white',
          color: 'grey.800',
        })}
  >
    {children}
  </Box>
);

interface ApplicationStepCardProps {
  title: string;
  children: ReactNode;
  status: 'active' | 'done' | 'todo';
  stepNumber: number;
  activeBadgeLabel: string;
}

export const ApplicationStepCard = ({
  activeBadgeLabel,
  children,
  status,
  stepNumber,
  title,
}: ApplicationStepCardProps) => {
  const [isExpanded, setIsExpanded] = useState(status === 'active');

  /**
   * Close the card when this step is marked as done
   * (notably after sending the KYB documents in applicationRegistration)
   */
  useEffect(() => {
    if (status === 'done') {
      setIsExpanded(false);
    } else if (status === 'active') {
      setTimeout(() => {
        setIsExpanded(true);
      }, 700);
    }
  }, [status]);

  return (
    <SunshineCard
      {...(status === 'active' && {
        borderColor: 'grey.800',
      })}
      overflow="hidden"
      padding="0"
    >
      <Flex
        {...(status !== 'active' && {
          _hover: {
            '>svg': {
              opacity: 1,
              transform: `${isExpanded ? 'rotate(180deg)' : 'translateY(2px)'}`,
            },
            backgroundColor: 'grey.100',
          },
          onClick: () => setIsExpanded(!isExpanded),
          role: 'button',
          transition: 'all 250ms ease-out',
        })}
        alignItems="center"
        borderBottomColor="grey.300"
        borderBottomStyle="solid"
        borderBottomWidth={isExpanded ? '1px' : '0px'}
        padding="space-16"
      >
        {(() => {
          switch (status) {
            case 'done':
              return <SquircleIcon backgroundColor="green.300" name="tick" />;
            case 'active':
              return <SquircleNumber active>{stepNumber}</SquircleNumber>;
            default:
              return (
                <SquircleNumber active={false}>{stepNumber}</SquircleNumber>
              );
          }
        })()}

        <SunshineCard.Title
          {...(status === 'done' && {
            color: 'grey.600',
          })}
          marginLeft="space-16"
        >
          {title}
        </SunshineCard.Title>

        <Spacer />

        {status === 'active' ? (
          <Badge colorScheme="blue" size="sm">
            {activeBadgeLabel}
          </Badge>
        ) : (
          <SunshineIcon
            color="grey.600"
            name="chevron-down"
            opacity={isExpanded ? 1 : 0}
            size="icon-24"
            transform={`translateY(0px) ${isExpanded ? 'rotate(180deg)' : ''}`}
            transition="all 150ms ease-out"
          />
        )}
      </Flex>

      <Collapse in={isExpanded}>
        <SunshineCard.Content padding="space-16">
          {children}
        </SunshineCard.Content>
      </Collapse>
    </SunshineCard>
  );
};
